import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  BelgeIkon,
  IskeleIkon,
  KalkanIkon,
  OkSagIkon,
  OnayIkon,
  RevizeIkon,
  SiralaIkon,
} from '../lib/ikon';
import { api } from '../lib/api';
import { RutinKontrol } from './FazAksiyon';

/**
 * İŞLERİM — ŞEFİN AKSİYON KUYRUĞU (devir dokümanı §6.7, prototip `vIslerim`).
 *
 * ⚠️ Saha kullanıcısının "İşlerim"i ile AYNI EKRAN DEĞİLDİR. O, kişiye atanmış
 *    işlerin listesidir (§6.11, mobil). Bu, ŞEFİN yapması gereken üç iştir.
 *
 * Bu ekran "Bildirimler"in yerine kondu ve gerekçesi doğrudan bir kullanıcı
 * şikâyeti: *"Bildirimler pasif bir liste idi — tıklayınca bir yere gitmiyordu.
 * Yerine aksiyon listesi kondu."*
 *
 * Bu yüzden BURADAKİ HER SATIR BİR YERE GİDER. Rutin kontrol satırı iş detayına
 * değil, DOĞRUDAN kontrol pop-up'ına açılır (§6.7 tablosu) — şef listeye dönüp
 * tekrar aramak zorunda kalmasın.
 */

interface Satir {
  id: string;
  isEmriNo: string | null;
  /** Teslimde giriliyor — bekleyen işte boş olabilir. */
  iskeleOnayNo: string | null;
  tanim: string;
  unite: string;
  /** İKİ AYRI TOPLAM — karışık iş tek sayıya indirgenmez (02.08.2026). */
  metrajM3: number;
  metrajM2: number;
  /** m³ + m² × 4 — tek sayı gerektiğinde (pano metriği, aylık kümülatif). */
  esdegerM3: number;
  /** İşin AÇILDIĞI an — sıralamanın ve satırdaki tarihin kaynağı. */
  olusturmaZamani: string;
  not: string;
}

/**
 * SIRALAMA — varsayılan ESKİDEN YENİYE, ÜÇ BAŞLIK İÇİN ORTAK.
 *
 * ⚠️ Varsayılan tesadüfi değil: K6 "öncelik alanı yoktur, işler FIFO sırayla
 *    işlenir" diyor. En eski iş en üstte durur. Ters sıralama bir GÖRÜNÜM
 *    tercihi — işin sırasını değiştirmez, "en son ne geldi" sorusunu cevaplar.
 *
 * ⚠️ "Haftalık rutin kontroller" başlığı bundan MUAF: orası kontrol edilmeyen
 *    gün sayısına göre sıralı (en geciken en üstte) ve o sıralama bir uyarı
 *    mekanizmasıdır — tarihe çevirmek en acil işi listenin ortasına gömerdi.
 */
type Yon = 'asc' | 'desc';

interface Kuyruk {
  foto: Satir[];
  kontrol: Satir[];
  onay: Satir[];
  /** Revize bitti, şef kontrol & onayı bekliyor — §4.5. */
  revize: Satir[];
  /** Kişiye ATANMIŞ saha işleri — söküm, revize, kurulum, keşif. */
  saha: Satir[];
  toplam: number;
}

export function IslerimKuyruk({ onIsSec }: { onIsSec: (id: string) => void }) {
  const [k, setK] = useState<Kuyruk | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [yon, setYon] = useState<Yon>('asc');
  /** Rutin kontrol pop-up'ı — satırdan doğrudan açılır. */
  const [kontrolIsi, setKontrolIsi] = useState<Satir | null>(null);

  const yukle = useCallback(() => {
    api
      .get<Kuyruk>('/islerim-kuyruk')
      .then(setK)
      .catch((e: Error) => setHata(e.message));
  }, []);

  /* Sıralama İSTEMCİDE: ağa gitmek çevrimdışında listeyi sıralanamaz yapardı (T1). */
  const sirala = useMemo(() => {
    const z = (s: Satir) => new Date(s.olusturmaZamani).getTime();
    return (l: Satir[]) => [...l].sort((a, b) => (yon === 'asc' ? z(a) - z(b) : z(b) - z(a)));
  }, [yon]);

  useEffect(yukle, [yukle]);

  if (hata) return <p className="m-6 rounded-(--radius-mod) bg-crit/10 p-4 text-sm text-crit">{hata}</p>;
  if (!k) return <p className="m-6 text-sm text-wait">Yükleniyor…</p>;

  return (
    <div className="mx-auto max-w-4xl p-4 lg:p-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
        <p className="max-w-xl rounded-(--radius-mod) bg-surface-2 px-4 py-2.5 text-xs leading-relaxed text-wait">
          Şef & formen yapılacakları: girilecek iş emri fotoları, haftalık kontroller,
          kurulum onayları, revize onayları. <strong>Her satır bir yere gider</strong> — bu
          bir bildirim listesi değil, aksiyon listesidir.
        </p>
        <button
          onClick={() => setYon((v) => (v === 'asc' ? 'desc' : 'asc'))}
          title={
            yon === 'asc'
              ? 'Eskiden yeniye (FIFO) — tersine çevirmek için tıklayın'
              : 'Yeniden eskiye — FIFO sırasına dönmek için tıklayın'
          }
          aria-label={`Tarihe göre sıralama: ${yon === 'asc' ? 'eskiden yeniye' : 'yeniden eskiye'}`}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-(--radius-mod) border border-line-2 px-2.5 py-1.5 text-xs font-medium text-wait hover:border-navy hover:text-navy"
        >
          <SiralaIkon yon={yon} boyut={16} />
          {yon === 'asc' ? 'Eski → yeni' : 'Yeni → eski'}
        </button>
      </div>

      {k.toplam === 0 && (
        <p className="rounded-(--radius-mod) border border-ok/25 bg-ok/5 px-4 py-8 text-center text-sm text-ok">
          Bekleyen aksiyonun yok — üç başlık da temiz.
        </p>
      )}

      <Bolum
        baslik="Girilmesi gereken iş emri fotoları"
        aciklama="İş emrisiz açılan işler — kâğıt fotosu olmadan KAPANAMAZ (K3)."
        satirlar={sirala(k.foto)}
        serit="border-l-warn"
        kutu="bg-warn-bg text-warn"
        ikon={<BelgeIkon boyut={18} />}
        onSec={(s) => onIsSec(s.id)}
      />

      <Bolum
        baslik="Haftalık kontroller"
        aciklama="7 günü geçmiş kurulu iskeleler — en geciken en üstte."
        satirlar={k.kontrol}
        serit="border-l-info"
        kutu="bg-info-bg text-info"
        ikon={<KalkanIkon boyut={18} />}
        // §6.7: bu satır iş detayına değil, doğrudan kontrol pop-up'ına gider.
        onSec={setKontrolIsi}
      />

      <Bolum
        baslik="Kurulum yeşil kart onay & kontrolü"
        aciklama={'"Ben kurdum" denen işler — 3 fotoğraf + nihai boyut + onay.'}
        satirlar={sirala(k.onay)}
        serit="border-l-navy"
        kutu="bg-navy-bg text-navy"
        ikon={<OnayIkon boyut={18} />}
        onSec={(s) => onIsSec(s.id)}
      />

      {/*
        ⚠️ BEŞİNCİ BAŞLIK — masaüstünde şef "İşlerim" yerine BU kuyruğu görüyor
           (§6.7); saha listesi telefonda kalıyor (§6.11). Şefe söküm emri
           verildiğinde iş onun masaüstündeki hiçbir ekranında görünmüyordu.
      */}
      <Bolum
        baslik="Sana atanmış saha işleri"
        aciklama="Söküm, revize, kurulum, keşif — üzerinde senin adın olan işler."
        satirlar={sirala(k.saha)}
        serit="border-l-ok"
        kutu="bg-ok-bg text-ok"
        ikon={<IskeleIkon boyut={18} />}
        onSec={(s) => onIsSec(s.id)}
      />

      {/*
        ⚠️ DÖRDÜNCÜ BAŞLIK — §6.7 üç başlık sayar, bu ondan sapma ve bilinçli.
           Eksikliği gerçek bir kayıptı: ekip "revizeyi bitirdik" diyordu, iş
           `revize` durumunda kalıyordu ve ŞEFİN HİÇBİR EKRANINDA görünmüyordu.
           §4.5 revize onayını şefin işi sayar; şefin işi bu ekranda durmalı.
      */}
      <Bolum
        baslik="Revize kontrol & onayı"
        aciklama="Revizesi bitmiş işler — kontrol et, boyut değiştiyse güncelle, onayla."
        satirlar={sirala(k.revize)}
        serit="border-l-stok"
        kutu="bg-stok-bg text-stok"
        ikon={<RevizeIkon boyut={18} />}
        onSec={(s) => onIsSec(s.id)}
      />

      {kontrolIsi && (
        <RutinKontrol
          jobId={kontrolIsi.id}
          onKapat={() => setKontrolIsi(null)}
          onBitti={() => {
            setKontrolIsi(null);
            yukle();
          }}
        />
      )}
    </div>
  );
}

function Bolum({
  baslik,
  aciklama,
  satirlar,
  serit,
  kutu,
  ikon,
  onSec,
}: {
  baslik: string;
  aciklama: string;
  satirlar: Satir[];
  serit: string;
  kutu: string;
  ikon: ReactNode;
  onSec: (s: Satir) => void;
}) {
  return (
    <section className="mb-5">
      <h2 className="text-sm font-semibold text-navy">
        {baslik} <span className="num font-normal text-wait">{satirlar.length}</span>
      </h2>
      <p className="mb-2 text-xs text-wait">{aciklama}</p>

      {satirlar.length === 0 ? (
        <p className="rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod) px-4 py-5 text-center text-sm text-wait">
          Bu başlıkta iş yok.
        </p>
      ) : (
        <div className="space-y-1.5">
          {satirlar.map((s) => (
            <button
              key={s.id}
              onClick={() => onSec(s)}
              className={`flex w-full items-center gap-3 rounded-(--radius-kart) border border-l-[5px] border-line bg-surface p-3 text-left shadow-(--shadow-mod) hover:bg-ground ${serit}`}
            >
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[11px] ${kutu}`}>
                {ikon}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-ink">
                  <span className="num font-normal text-muted">{s.isEmriNo ?? '—'}</span>
                  {s.iskeleOnayNo && (
                    <span className="num font-normal text-muted"> · onay {s.iskeleOnayNo}</span>
                  )}{' · '}
                  {s.tanim}
                </span>
                <span className="block truncate text-xs text-muted">
                  {s.unite} · {s.not}
                  {/* Tarih GÖRÜNÜR: görünmeyen bir alana göre sıralamak,
                      listenin neden değiştiğini anlaşılmaz kılar. */}
                  {' · '}
                  <span className="num">
                    {new Date(s.olusturmaZamani).toLocaleDateString('tr-TR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: '2-digit',
                    })}
                  </span>
                </span>
              </span>
              <OkSagIkon boyut={15} className="shrink-0 text-faint" />
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
